import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Cookie, ChevronDown } from 'lucide-react';

const STORAGE_KEY = 'cookieConsent'; // 'accepted' | 'rejected'

/**
 * First-visit consent banner (GDPR / ePrivacy — AEPD & Garante).
 * The app only uses strictly-necessary technical storage (auth tokens,
 * language/theme preferences, this consent flag) — there are no analytics,
 * marketing or profiling cookies. Both choices are equally prominent.
 */
export default function CookieConsentBanner() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [path, setPath] = useState(() => (typeof window !== 'undefined' ? window.location.pathname : '/'));

  useEffect(() => {
    try {
      if (!localStorage.getItem(STORAGE_KEY)) setVisible(true);
    } catch {
      setVisible(true);
    }
    // Track the route so we never cover the legal page the user is reading — and
    // reappear when they leave it. The banner lives outside the router, so we
    // patch the History API (SPA <Link> nav uses pushState, which doesn't fire
    // popstate) in addition to popstate (back/forward).
    const update = () => setPath(window.location.pathname);
    const origPush = window.history.pushState;
    const origReplace = window.history.replaceState;
    window.history.pushState = function (...args: Parameters<typeof origPush>) { origPush.apply(this, args); update(); };
    window.history.replaceState = function (...args: Parameters<typeof origReplace>) { origReplace.apply(this, args); update(); };
    window.addEventListener('popstate', update);
    return () => {
      window.removeEventListener('popstate', update);
      window.history.pushState = origPush;
      window.history.replaceState = origReplace;
    };
  }, []);

  const decide = (choice: 'accepted' | 'rejected') => {
    try { localStorage.setItem(STORAGE_KEY, choice); } catch { /* ignore */ }
    setVisible(false);
  };

  // Never overlay the privacy/terms page itself — the user is there to read it.
  if (!visible || path === '/data-privacy') return null;

  return (
    <div
      role="dialog"
      aria-live="polite"
      aria-label={t('cookieConsent.title', 'Privacy e cookie')}
      className="fixed inset-x-0 bottom-0 z-[70] p-3 sm:p-5"
    >
      <div className="mx-auto max-w-3xl overflow-hidden rounded-2xl border border-border bg-card shadow-2xl">
        <div className="flex flex-col gap-4 p-5 sm:flex-row sm:items-start sm:gap-5 sm:p-6">
          <div
            className="flex size-11 shrink-0 items-center justify-center rounded-xl text-white"
            style={{ background: 'var(--gradient-agencium)' }}
          >
            <Cookie className="size-6" />
          </div>

          <div className="min-w-0 flex-1">
            <h2 className="font-display text-lg text-foreground">
              {t('cookieConsent.title', 'Privacy e cookie')}
            </h2>
            <p className="mt-1 text-sm leading-relaxed text-muted-foreground">
              {t(
                'cookieConsent.body',
                'Usiamo solo cookie e archiviazione tecnici necessari al funzionamento (accesso e preferenze). Per erogare le simulazioni trattiamo i tuoi dati tramite fornitori specializzati di elaborazione vocale e di analisi con intelligenza artificiale. Nessun cookie di marketing o profilazione.',
              )}{' '}
              <a href="/data-privacy" className="font-medium text-primary underline-offset-2 hover:underline">
                {t('cookieConsent.privacyLink', 'Privacy e termini')}
              </a>
            </p>

            <button
              type="button"
              onClick={() => setShowDetails((v) => !v)}
              className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-muted-foreground hover:text-foreground"
            >
              {t('cookieConsent.detailsToggle', 'Dettagli')}
              <ChevronDown className={`size-3.5 transition-transform ${showDetails ? 'rotate-180' : ''}`} />
            </button>

            {showDetails && (
              <div className="mt-3 rounded-lg border border-border bg-muted/40 p-3 text-xs leading-relaxed text-muted-foreground">
                <p className="font-semibold text-foreground">
                  {t('cookieConsent.essentialTitle', 'Cookie e archiviazione essenziali')}
                </p>
                <ul className="mt-1.5 ml-4 list-disc space-y-1">
                  <li>{t('cookieConsent.itemAuth', 'Token di sessione (mantieni l’accesso).')}</li>
                  <li>{t('cookieConsent.itemPrefs', 'Lingua e tema dell’interfaccia.')}</li>
                  <li>{t('cookieConsent.itemConsent', 'Memoria di questa scelta sui cookie.')}</li>
                </ul>
                <p className="mt-2">
                  {t('cookieConsent.noTracking', 'Non utilizziamo cookie di analisi, pubblicità o profilazione di terze parti.')}
                </p>
              </div>
            )}
          </div>

          <div className="flex shrink-0 gap-2 sm:flex-col">
            <button
              type="button"
              onClick={() => decide('accepted')}
              className="flex-1 rounded-full px-5 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:opacity-90 sm:flex-none"
              style={{ background: 'var(--gradient-agencium)' }}
            >
              {t('cookieConsent.accept', 'Accetta')}
            </button>
            <button
              type="button"
              onClick={() => decide('rejected')}
              className="flex-1 rounded-full border border-border bg-background px-5 py-2.5 text-sm font-semibold text-foreground transition hover:bg-accent sm:flex-none"
            >
              {t('cookieConsent.reject', 'Solo essenziali')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
